import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { find } from 'lodash'
import dayjs from 'dayjs'
import useNodeStatisticsResource from '../../../../shared/resources/api/v1/admin/useNodeStatisticsResource'
import StatisticsAreaChart from '../../../../shared/components/charts/StatisticsAreaChart'
import { TIME_RANGES } from '../../../../shared/constants/statisticsConfig'
import usePageVisibility from '../../../../shared/hooks/usePageVisibility'

const REFRESH_INTERVAL_MS = 60 * 1000

const percentageFormatter = (value) => value != null ? `${value.toFixed(1)}%` : ''

function NodeMetricCard({ node, metric, timeRange, initialData }) {
  const { teamSlug } = useParams()
  const isVisible = usePageVisibility()
  const { getStatistics } = useNodeStatisticsResource(teamSlug, node.id)
  const [data, setData] = useState(initialData || [])
  const [loading, setLoading] = useState(!initialData)
  const hasFetchedRef = useRef(!!initialData)
  const intervalRef = useRef(null)

  const fetchData = useCallback(() => {
    const selectedRange = find(TIME_RANGES, { value: timeRange })
    const after = dayjs().subtract(selectedRange.hours, 'hour').toISOString()
    const params = {
      interval: selectedRange.interval,
      after,
      limit: selectedRange.limit,
    }

    if (!hasFetchedRef.current) {
      setLoading(true)
    }
    getStatistics(metric, params)
      .then((response) => {
        setData(response.statistics || [])
        hasFetchedRef.current = true
      })
      .catch(() => {
        setData([])
      })
      .finally(() => {
        setLoading(false)
      })
  }, [metric, timeRange, getStatistics])

  const fetchDataRef = useRef(fetchData)
  fetchDataRef.current = fetchData

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (isVisible) {
      intervalRef.current = setInterval(() => fetchDataRef.current(), REFRESH_INTERVAL_MS)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isVisible])

  return (
    <StatisticsAreaChart
      title={node.name}
      data={data}
      loading={loading}
      formatValue={percentageFormatter}
    />
  )
}

export default NodeMetricCard
